// Structures drawn as architectural section drawings: paper-white faces, ink lines, solid black poché where a
// section cuts through material. On the map the structures are whole; their cut sections are drawn in section.ts.
// Each structure is a list of primitives in painting order, in world units (gx, gy, gz).
import { PLACES } from './cloud';

// fills
export const F = { NONE: 0, PAPER: 1, POCHE: 2, GLASS: 3, FLOOR: 4, LEAF: 5, CORAL: 6, LIME: 7, COBALT: 8, YELLOW: 9 };
// kinds: 0 closed polygon, 1 open line, 2 circle
export interface Prim { k: number; p: number[]; f: number; s: number; w: number; r: number }
/** A chapter's hub (`hub`) shrinks and fades as its chapter opens; `cx`, `cy` is its centre. */
export interface Struct { key: number; prims: Prim[]; hub?: string; cx?: number; cy?: number }

class Sheet {
  prims: Prim[] = [];
  poly(p: number[], f: number, s = 1, w = 2): void { this.prims.push({ k: 0, p, f, s, w, r: 0 }); }
  line(p: number[], w = 1): void { this.prims.push({ k: 1, p, f: 0, s: 1, w, r: 0 }); }
  circle(x: number, y: number, z: number, r: number, f: number, w = 1, s = 1): void { this.prims.push({ k: 2, p: [x, y, z], f, s, w, r }); }
  quadX(x: number, y0: number, y1: number, z0: number, z1: number, f: number, s = 1, w = 2): void {
    this.poly([x, y0, z0, x, y1, z0, x, y1, z1, x, y0, z1], f, s, w);
  }
  quadY(y: number, x0: number, x1: number, z0: number, z1: number, f: number, s = 1, w = 2): void {
    this.poly([x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1], f, s, w);
  }
  quadZ(z: number, x0: number, x1: number, y0: number, y1: number, f: number, s = 1, w = 2): void {
    this.poly([x0, y0, z, x1, y0, z, x1, y1, z, x0, y1, z], f, s, w);
  }
  /** The three faces the camera sees. */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, f = F.PAPER, w = 2): void {
    this.quadZ(z1, x0, x1, y0, y1, f, 1, w);
    this.quadX(x1, y0, y1, z0, z1, f, 0.9, w);
    this.quadY(y1, x0, x1, z0, z1, f, 0.8, w);
  }
}

const place = (id: string) => PLACES.find((p) => p.id === id)!;

export function buildStructures(): Struct[] {
  const out: Struct[] = [];

  // ---- gate ----
  {
    const p = place('field'), s = new Sheet();
    s.box(p.x - 0.3, p.y - 3.3, 0, p.x + 0.3, p.y - 2.7, 3.8);
    s.box(p.x - 0.3, p.y + 2.7, 0, p.x + 0.3, p.y + 3.3, 3.8);
    s.box(p.x - 0.3, p.y - 3.3, 3.8, p.x + 0.3, p.y + 3.3, 4.3);
    out.push({ key: p.x + p.y, prims: s.prims });
  }

  // ---- well: a ring with a black shaft, two posts and a small gabled roof ----
  {
    const p = place('well'), s = new Sheet(), rr = 2, h = 1.1, N = 28;
    const band: number[] = [];
    const a0 = -Math.PI / 4, a1 = (3 * Math.PI) / 4;
    for (let i = 0; i <= 14; i++) { const a = a0 + ((a1 - a0) * i) / 14; band.push(p.x + rr * Math.cos(a), p.y + rr * Math.sin(a), 0); }
    for (let i = 14; i >= 0; i--) { const a = a0 + ((a1 - a0) * i) / 14; band.push(p.x + rr * Math.cos(a), p.y + rr * Math.sin(a), h); }
    s.poly(band, F.PAPER, 0.85, 2);
    const ring = (rad: number, z: number): number[] => {
      const o: number[] = [];
      for (let i = 0; i < N; i++) { const a = (i / N) * Math.PI * 2; o.push(p.x + rad * Math.cos(a), p.y + rad * Math.sin(a), z); }
      return o;
    };
    s.poly(ring(rr, h), F.PAPER, 1, 2);
    s.poly(ring(1.35, h), F.POCHE, 1, 1);
    s.box(p.x - 1.75, p.y - 0.15, h, p.x - 1.45, p.y + 0.15, 3.6);
    s.line([p.x, p.y, 3.6, p.x, p.y, 1.7], 1);
    s.box(p.x + 1.45, p.y - 0.15, h, p.x + 1.75, p.y + 0.15, 3.6);
    const zw = 3.6, zr = 4.7, hy = 1.7, xa = p.x - 2.1, xb = p.x + 2.1;
    s.poly([xb, p.y - hy, zw, xb, p.y + hy, zw, xb, p.y, zr], F.PAPER, 0.9, 2);
    s.poly([xa, p.y - hy, zw, xb, p.y - hy, zw, xb, p.y, zr, xa, p.y, zr], F.PAPER, 0.95, 2);
    s.poly([xa, p.y, zr, xb, p.y, zr, xb, p.y + hy, zw, xa, p.y + hy, zw], F.PAPER, 0.8, 2);
    out.push({ key: p.x + p.y, prims: s.prims });
  }

  // ---- greenhouse: a whole glasshouse with a gabled roof, the tree and plant rows showing through the glass ----
  {
    const p = place('greenhouse'), s = new Sheet();
    const x0 = p.x - 8, x1 = p.x + 8, y0 = p.y - 5, y1 = p.y + 5, ym = p.y, wh = 4, rh = 7;
    s.quadZ(0, x0, x1, y0, y1, F.FLOOR, 1, 1);
    s.quadY(y0 + 0.12, x0, x1, 0, wh, F.GLASS, 1, 1); // back wall, seen from inside
    s.poly([x0 + 0.12, y0, 0, x0 + 0.12, y0, wh, x0 + 0.12, ym, rh, x0 + 0.12, y1, wh, x0 + 0.12, y1, 0], F.GLASS, 1, 1);
    // inside: a bench, two rows of plants, two machines in branded apparel, the tree at the centre
    s.box(x0 + 1.2, y0 + 1.2, 0, x0 + 3.2, y0 + 2.4, 0.7, F.PAPER, 1);
    for (const row of [p.y - 2.8, p.y + 2.8]) {
      for (let xx = x0 + 2; xx <= x1 - 1.5; xx += 2.3) if (Math.abs(xx - p.x) > 3.2) s.circle(xx, row, 0.7, 0.6, F.LEAF, 1);
    }
    for (const mx of [x0 + 4.2, x1 - 4.2]) s.box(mx - 0.5, p.y - 1.3, 0, mx + 0.5, p.y - 0.3, 1.4, F.LIME, 1);
    s.line([p.x, ym, 0, p.x, ym, 3.4], 3);
    s.circle(p.x, ym, 4.6, 2, F.LEAF, 1);
    // glazed front wall and end wall, then both roof slopes, over the inside
    s.quadY(y1, x0, x1, 0, wh, F.GLASS, 1, 1);
    for (let xx = x0; xx <= x1 + 0.01; xx += 2) s.line([xx, y1, 0, xx, y1, wh], 1);
    s.poly([x1, y0, 0, x1, y0, wh, x1, ym, rh, x1, y1, wh, x1, y1, 0], F.GLASS, 1, 1);
    for (let yy = y0; yy <= y1 + 0.01; yy += 2.5) s.line([x1, yy, 0, x1, yy, wh + ((rh - wh) * (5 - Math.abs(yy - ym))) / 5], 1);
    s.poly([x0, y0, wh, x1, y0, wh, x1, ym, rh, x0, ym, rh], F.GLASS, 1, 1);
    s.poly([x0, ym, rh, x1, ym, rh, x1, y1, wh, x0, y1, wh], F.GLASS, 1, 1);
    for (let xx = x0; xx <= x1 + 0.01; xx += 2) { s.line([xx, y0, wh, xx, ym, rh], 1); s.line([xx, ym, rh, xx, y1, wh], 1); }
    s.line([x0, ym, rh, x1, ym, rh], 3);
    s.line([x0, y1, wh, x1, y1, wh], 2);
    out.push({ key: p.x + p.y, prims: s.prims });
  }

  // ---- desk: an open pavilion with a desk, a calendar board and a kid with one earbud ----
  {
    const p = place('desk'), s = new Sheet();
    const x0 = p.x - 4.8, x1 = p.x + 4.8, y0 = p.y - 3.4, y1 = p.y + 3.4;
    const post = (x: number, y: number): void => s.box(x - 0.18, y - 0.18, 0, x + 0.18, y + 0.18, 4);
    post(x0, y0);
    s.box(x0 + 0.4, p.y - 2.4, 1.3, x0 + 0.6, p.y + 2.4, 3.6);
    for (let i = 0; i <= 6; i++) s.line([x0 + 0.62, p.y - 2.4 + i * 0.8, 1.3, x0 + 0.62, p.y - 2.4 + i * 0.8, 3.6], 1);
    for (let j = 0; j <= 4; j++) s.line([x0 + 0.62, p.y - 2.4, 1.3 + j * 0.575, x0 + 0.62, p.y + 2.4, 1.3 + j * 0.575], 1);
    s.box(p.x - 2.4, p.y - 0.2, 0, p.x + 2.1, p.y + 1.6, 1.3);
    s.box(p.x - 0.4, p.y - 1.5, 0, p.x + 0.4, p.y - 0.8, 1.3, F.COBALT, 1);
    s.circle(p.x, p.y - 1.15, 1.75, 0.38, F.PAPER, 2);
    s.circle(p.x + 0.32, p.y - 1.15, 1.72, 0.1, F.POCHE, 0);
    post(x1, y0); post(x0, y1); post(x1, y1);
    s.quadX(x1 + 0.3, y0 - 0.3, y1 + 0.3, 4, 4.45, F.PAPER, 0.9, 2);
    s.quadY(y1 + 0.3, x0 - 0.3, x1 + 0.3, 4, 4.45, F.PAPER, 0.8, 2);
    s.quadZ(4.45, x0 - 0.3, x1 + 0.3, y0 - 0.3, y1 + 0.3, F.GLASS, 1, 3); // a see-through roof plane, so the desk shows
    out.push({ key: p.x + p.y, prims: s.prims });
  }

  // ---- one hub for each design chapter: a single simple structure that spreads into the chapter's projects ----
  {
    const hubs: [string, (s: Sheet, x: number, y: number) => void][] = [
      ['architecture', (s, x, y) => { // a stepped tower on a slab
        s.box(x - 2.6, y - 2.6, 0, x + 2.6, y + 2.6, 0.5);
        s.box(x - 2, y - 2, 0.5, x + 0.4, y + 2, 2.6);
        s.box(x - 0.9, y - 1.4, 2.6, x + 1.6, y + 1.4, 5);
        s.box(x + 0.5, y - 2.2, 0.5, x + 2.4, y - 0.4, 1.6);
      }],
      ['planning', (s, x, y) => { // a site plan: lots round a street cross
        s.box(x - 2.6, y - 2.6, 0, x + 2.6, y + 2.6, 0.35);
        for (const [bx, by] of [[-2.2, -2.2], [0.35, -2.2], [-2.2, 0.35], [0.35, 0.35]]) s.box(x + bx, y + by, 0.35, x + bx + 1.8, y + by + 1.8, 0.35 + 0.8 + ((bx + by) % 3 > 0 ? 0.9 : 0.2), F.PAPER, 1);
      }],
      ['interviews', (s, x, y) => { // a screen with a row of seats before it
        s.box(x - 2.6, y - 2.6, 0, x + 2.6, y + 2.6, 0.3);
        s.box(x - 2.2, y - 1.6, 0.3, x - 1.9, y + 1.6, 3.6);
        for (let i = 0; i < 3; i++) s.box(x - 0.6 + i * 1.1, y - 1.5, 0.3, x - 0.1 + i * 1.1, y + 1.5, 0.9, F.PAPER, 1);
      }],
    ];
    for (const [id, draw] of hubs) {
      const p = place(id), s = new Sheet();
      draw(s, p.x, p.y);
      out.push({ key: p.x + p.y, prims: s.prims, hub: id, cx: p.x, cy: p.y });
    }
  }

  return out.sort((a, b) => a.key - b.key);
}
